import { EvaluationFieldProps } from "../EvaluationField";
import { ExcludeNotApplicable } from "../EvaluationResult/EvaluationResult.decl";

import {
  ComputeNextValueArgs,
  CreateEvaluationCategory,
  CreateEvaluationInput,
  CreateEvaluationQuestion,
  GeneratedAIAnswersMap,
  NullAnswers,
  QuestionPossibleBinaryAnswer,
  QuestionPossibleBinaryAnswers,
  QuestionPossibleRatingAnswer,
  QuestionPossibleRatingAnswers,
} from "./EvaluationCreateForm.decl";

import {
  BinaryAnswer,
  BinaryAnswerValue,
  RatingAnswer,
  RatingAnswerValue,
  ScoreCardFragment,
} from "@/graphql/generated/graphql";
import { Unpacked } from "@/helpers/types/types.helpers";

export function hasNotApplicableAnswer(question: CreateEvaluationQuestion) {
  return (question.binaryAnswer ?? question.ratingAnswer) === "NOT_APPLICABLE";
}

export function hasPossiblyNotApplicable(question: CreateEvaluationQuestion) {
  return (
    question.questionPossibleRatingAnswers ??
    question.questionPossibleBinaryAnswers ??
    []
  ).some((answer) => answer.value === "NOT_APPLICABLE");
}

export function getDescription(question: CreateEvaluationQuestion) {
  const found = (
    question.questionPossibleBinaryAnswers ??
    question.questionPossibleRatingAnswers ??
    []
  ).find(
    (answer) =>
      answer.value === (question.binaryAnswer ?? question.ratingAnswer)
  );

  if (!found) {
    return;
  }

  if (question.questionAiEnabled) {
    return "";
  }

  return found.answerGuide ?? "";
}

export function getRating(question: CreateEvaluationQuestion) {
  if (
    question.binaryAnswer === "NOT_APPLICABLE" ||
    question.ratingAnswer === "NOT_APPLICABLE"
  ) {
    return null;
  }

  return question.binaryAnswer ?? question.ratingAnswer;
}

export function buildBinaryRatingScale(
  answers: QuestionPossibleBinaryAnswers
): EvaluationFieldProps<
  ExcludeNotApplicable<BinaryAnswerValue>
>["ratingScale"] {
  return answers.reduce<
    EvaluationFieldProps<ExcludeNotApplicable<BinaryAnswerValue>>["ratingScale"]
  >(
    (accu, answer) => ({
      ...accu,
      [answer.value]: answer.points,
    }),
    {
      YES: NaN,
      NO: NaN,
    }
  );
}

export function buildMultipleRatingScale(
  answers: QuestionPossibleRatingAnswers
): EvaluationFieldProps<
  ExcludeNotApplicable<RatingAnswerValue>
>["ratingScale"] {
  return answers.reduce<
    EvaluationFieldProps<ExcludeNotApplicable<RatingAnswerValue>>["ratingScale"]
  >(
    (accu, answer) => ({
      ...accu,
      [answer.value]: answer.points,
    }),
    {
      VERY_POOR: NaN,
      POOR: NaN,
      FAIR: NaN,
      GOOD: NaN,
      EXCELLENT: NaN,
    }
  );
}

export function initCategories(
  categories: ScoreCardFragment["categories"]
): CreateEvaluationInput["categories"] {
  return categories.map((category) => ({
    categoryName: category.name,
    categoryOrder: category.order,
    scoreCardCategoryId: category.ID,
    questions: category.questions.map((question) => {
      if (question.__typename === "ScoreCardRatingQuestion") {
        return {
          scoreCardQuestionId: question.ID,
          ratingAnswer: null,
          questionPossibleRatingAnswers: question.possibleRatingAnswers.map(
            (answer) => ({
              value: answer.ratingValue,
              points: answer.points,
              answerGuide: answer.answerGuide,
            })
          ),
          questionTotalPoints: question.totalPoints,
          questionText: question.text,
          questionOrder: question.order,
          questionAiEnabled: question.isAIEnabled,
          predefinedAIQuestionId: question.predefinedAIQuestionId,
        };
      }

      if (question.__typename === "ScoreCardBinaryQuestion") {
        return {
          scoreCardQuestionId: question.ID,
          binaryAnswer: null,
          questionPossibleBinaryAnswers: question.possibleBinaryAnswers.map(
            (answer) => ({
              value: answer.binaryValue,
              points: answer.points,
              answerGuide: answer.answerGuide,
            })
          ),
          questionTotalPoints: question.totalPoints,
          questionText: question.text,
          questionOrder: question.order,
          questionAiEnabled: question.isAIEnabled,
          predefinedAIQuestionId: question.predefinedAIQuestionId,
        };
      }

      throw new Error("Invalid question type");
    }),
  }));
}

export function mergeExistingFormCategoriesWithGeneratedAIAnswers(
  categories: CreateEvaluationInput["categories"],
  generatedAIAnswers?: GeneratedAIAnswersMap
): CreateEvaluationInput["categories"] {
  return categories.map((category) => ({
    ...category,
    questions: category.questions.map((question) => {
      const generatedAnswer =
        generatedAIAnswers?.[question.scoreCardQuestionId];

      if ("questionPossibleRatingAnswers" in question) {
        return {
          ...question,
          ...(generatedAnswer &&
            "ratingAnswer" in generatedAnswer && {
              ratingAnswer: generatedAnswer.ratingAnswer,
              feedback: generatedAnswer.feedback,
            }),
        };
      }

      return {
        ...question,
        ...(generatedAnswer &&
          "binaryAnswer" in generatedAnswer && {
            binaryAnswer: generatedAnswer.binaryAnswer,
            feedback: generatedAnswer.feedback,
          }),
      };
    }),
  }));
}

export function computeNextValue({
  currentQuestionValue,
  previousQuestionValue,
}: ComputeNextValueArgs) {
  if (currentQuestionValue === previousQuestionValue) {
    return null;
  }

  return currentQuestionValue;
}

export function initSet(categories: CreateEvaluationInput["categories"]) {
  if (!categories.length) {
    return;
  }

  return categories.map((category) => category.categoryOrder);
}

// TODO: add a transformer here from ScoreCardRatingQuestion to QuestionPossibleRatingAnswers
function transformRatingQuestion(
  // questions: Unpacked<ScoreCardRatingQuestion["possibleRatingAnswers"]>[],
  questions: RatingAnswer[]
): QuestionPossibleRatingAnswers {
  return questions.map((question) => ({
    value: question.ratingValue,
    answerGuide: question.answerGuide,
    points: question.points,
  }));
}

function transformBinaryQuestion(
  // questions: Unpacked<ScoreCardRatingQuestion["possibleRatingAnswers"]>[],
  questions: BinaryAnswer[]
): QuestionPossibleBinaryAnswers {
  return questions.map((question) => ({
    value: question.binaryValue,
    answerGuide: question.answerGuide,
    points: question.points,
  }));
}

export function initRatingScales(scoreCard: ScoreCardFragment) {
  return scoreCard.categories.map((category) =>
    category.questions.map((question) => {
      if (question.__typename === "ScoreCardRatingQuestion") {
        return buildMultipleRatingScale(
          transformRatingQuestion(question.possibleRatingAnswers)
        );
      }

      if (question.__typename === "ScoreCardBinaryQuestion") {
        return buildBinaryRatingScale(
          transformBinaryQuestion(question.possibleBinaryAnswers)
        );
      }

      throw new Error("Invalid question type");
    })
  );
}

export function extractNullQuestionIndexes(
  question: CreateEvaluationQuestion,
  index: number
) {
  const value = question.binaryAnswer ?? question.ratingAnswer;

  return value === null || value === undefined ? [index] : [];
}

export function extractCategoryAndQuestionIndexes(
  category: CreateEvaluationCategory,
  index: number
): [number, number[]][] {
  const extractedQuestionIndexes = category.questions.flatMap(
    extractNullQuestionIndexes
  );

  if (!extractedQuestionIndexes.length) {
    // Filter out categories which has no null questions.
    return [];
  }

  return [[index, extractedQuestionIndexes]];
}

export function extractQuestions(nullAnswer: Unpacked<NullAnswers>) {
  const [, questions] = nullAnswer;

  return questions;
}

export function getHighestPoints(
  highest: number,
  answer: QuestionPossibleRatingAnswer | QuestionPossibleBinaryAnswer
) {
  return answer.points > highest ? answer.points : highest;
}
